import type {
  UndefinedInitialDataInfiniteOptions,
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import dayjs from 'dayjs';
import type { ResponseError } from 'superagent';

const msUntilEndOfDay = dayjs().endOf('day').diff(dayjs(), 'ms');

// Helper to make typing useQueryParams less verbose.
export type UseQueryParams<TReturnType = unknown, TError = ResponseError> = Omit<
  UseQueryOptions<TReturnType, TError>,
  'queryKey' | 'queryFn' | 'initialData'
> & {
  initialData?: () => undefined;
};

// Helper to make typing useQueryParams less verbose when using useInfiniteQuery.
export type UseInfiniteQueryParams<TReturnType = unknown, TError = ResponseError> = Omit<
  UndefinedInitialDataInfiniteOptions<TReturnType, TError>,
  'queryKey' | 'queryFn'
>;

// Helper to make typing useMutationParams less verbose.
export type UseMutationParams<
  TReturnType = unknown,
  TParams = unknown,
  TError = ResponseError,
> = Omit<UseMutationOptions<TReturnType, TError, TParams>, 'mutationFn'>;

export const isResponseError = (error: Error): error is ResponseError => {
  return error && 'status' in error;
};

// Cache presets for react-query.
export const cachePresets = {
  live: {
    staleTime: 0,
    gcTime: 1 * 60 * 1000, // 1 minute
  },
  short: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  },
  stable: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
  },
  daily: {
    staleTime: msUntilEndOfDay,
    gcTime: msUntilEndOfDay,
  },
  static: {
    staleTime: Infinity,
    gcTime: Infinity,
  },
};
